import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { useChatbotStore } from '@stores/chatbot/chatbotStore';
import LocalStorageKey from '@enums/LocalStorageKey';

export interface WorkspaceContextChange {
  workspaceId: string | null;
  companyName: string;
  displayName: string;
  timestamp: number;
  tabId: string;
}

// Generate a unique ID for this tab
const TAB_ID = `tab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// Full localStorage key with prefix
const STORAGE_KEY = `${LocalStorageKey.APP_STORAGE_PREFIX}_${LocalStorageKey.WORKSPACE_CONTEXT_CHANGE}`;

/**
 * Hook that detects when workspace context changes in another browser tab
 * and automatically syncs the current tab to the new context.
 *
 * This prevents the dangerous scenario where:
 * - Tab A switches to Workspace Y
 * - Tab B still shows Workspace X UI
 * - But Tab B's API calls would operate on Workspace Y (cookie changed)
 *
 * When a cross-tab workspace change is detected, this hook:
 * 1. Shows a blocking overlay (via isSyncing state)
 * 2. Calls loadUserProfile to get the new context from backend
 * 3. Clears React Query cache to prevent stale data
 * 4. Hides the overlay once sync is complete
 */
export function useWorkspaceSyncGuard() {
  const { currentUser, loadUserProfile } = useAuth();
  const [isSyncing, setIsSyncing] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState<string>('');
  const currentCompanyNameRef = useRef(currentUser?.companyName);

  // Keep ref in sync with current user
  useEffect(() => {
    currentCompanyNameRef.current = currentUser?.companyName;
  }, [currentUser?.companyName]);

  // Sync context when another tab switches workspaces
  const handleContextChange = useCallback(
    async (change: WorkspaceContextChange) => {
      // Ignore changes from this tab
      if (change.tabId === TAB_ID) return;

      // Ignore if we're already on the same context
      if (change.companyName === currentCompanyNameRef.current) return;

      useChatbotStore.getState().detachFromStream();

      setNewWorkspaceName(change.displayName || 'another workspace');
      setIsSyncing(true);

      try {
        // Reload user profile from backend - this will get the new context
        // since the cookie has already been updated by the other tab
        await loadUserProfile(null, true);

        // Brief delay to ensure UI updates before removing overlay
        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (error) {
        console.error('[WorkspaceSyncGuard] Failed to sync context:', error);
        // On error, force a full page reload as fallback
        window.location.reload();
      } finally {
        setIsSyncing(false);
        setNewWorkspaceName('');
      }
    },
    [loadUserProfile]
  );

  // Listen for storage events (fired when another tab modifies localStorage)
  useEffect(() => {
    const handleStorageEvent = (event: StorageEvent) => {
      // Only handle our specific key
      if (event.key !== STORAGE_KEY) return;
      if (!event.newValue) return;

      try {
        const change: WorkspaceContextChange = JSON.parse(event.newValue);
        handleContextChange(change);
      } catch (error) {
        console.error('[WorkspaceSyncGuard] Failed to parse storage event:', error);
      }
    };

    window.addEventListener('storage', handleStorageEvent);
    return () => window.removeEventListener('storage', handleStorageEvent);
  }, [handleContextChange]);

  return {
    isSyncing,
    newWorkspaceName,
  };
}

/**
 * Broadcast a workspace context change to other tabs.
 * Call this when the user switches workspaces.
 */
export function broadcastWorkspaceChange(
  workspaceId: string | null,
  companyName: string,
  displayName: string
): void {
  const change: WorkspaceContextChange = {
    workspaceId,
    companyName,
    displayName,
    timestamp: Date.now(),
    tabId: TAB_ID,
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(change));
}

export default useWorkspaceSyncGuard;
